import { LineLoading } from "@/components/loading";
import { getFirstPermittedPath } from "@/constants/permissions";
import DashboardLayout from "@/layouts/dashboard";
import AuthGuard from "@/routes/components/auth-guard";
import { usePermissions } from "@/store/userStore";
import { Suspense, lazy } from "react";
import type { RouteObject } from "react-router";
import { Navigate } from "react-router";

function PermittedHomepage() {
	const permissions = usePermissions();
	if (!permissions) return null;
	const homepage = getFirstPermittedPath(permissions);
	return <Navigate to={homepage} replace />;
}

// dashboard
const WorkbenchPage = lazy(() => import("@/pages/dashboard/workbench"));
const AnalysisPage = lazy(() => import("@/pages/dashboard/analysis"));

// custom components
const AnimatePage = lazy(() => import("@/pages/components/animate"));
const ScrollPage = lazy(() => import("@/pages/components/scroll"));
const MultiLanguagePage = lazy(() => import("@/pages/components/multi-language"));
const IconPage = lazy(() => import("@/pages/components/icon"));
const UploadPage = lazy(() => import("@/pages/components/upload"));
const ChartPage = lazy(() => import("@/pages/components/chart"));
const ToastPage = lazy(() => import("@/pages/components/toast"));
const ClipboardPage = lazy(() => import("@/pages/functions/clipboard"));
const TokenExpiredPage = lazy(() => import("@/pages/functions/token-expired"));

// error
const Page403 = lazy(() => import("@/pages/sys/error/Page403"));
const Page404 = lazy(() => import("@/pages/sys/error/Page404"));
const Page500 = lazy(() => import("@/pages/sys/error/Page500"));

// management
const ProfilePage = lazy(() => import("@/pages/management/user/profile"));
const AccountPage = lazy(() => import("@/pages/management/user/account"));
const OrganizationPage = lazy(() => import("@/pages/management/system/organization"));
const PermissioPage = lazy(() => import("@/pages/management/system/permission"));
const RolePage = lazy(() => import("@/pages/management/system/role"));
const UserPage = lazy(() => import("@/pages/management/system/user"));

// pw_management
const MerchantPage = lazy(() => import("@/pages/pw_management/merchant"));
const RatePage = lazy(() => import("@/pages/pw_management/rate"));
const PosEventPage = lazy(() => import("@/pages/pw_management/pos_event"));
const FiatDistributionPage = lazy(() => import("@/pages/pw_management/fiat_distribution"));
const DistributionPage = lazy(() => import("@/pages/pw_management/manual_distribution"));
const WalletsPage = lazy(() => import("@/pages/pw_management/wallets"));
const WithdrawalOrdersPage = lazy(() => import("@/pages/pw_management/withdrawal_orders"));
const CheckoutOrdersPage = lazy(() => import("@/pages/pw_management/checkout_orders"));
const PayoutGatewayBalancesPage = lazy(() => import("@/pages/pw_management/payout_gateway_balances"));

// menu level
const MenuLevel1a = lazy(() => import("@/pages/menu-level/menu-level-1a"));
const MenuLevel2a = lazy(() => import("@/pages/menu-level/menu-level-1b/menu-level-2a"));
const MenuLevel3a = lazy(() => import("@/pages/menu-level/menu-level-1b/menu-level-2b/menu-level-3a"));
const MenuLevel3b = lazy(() => import("@/pages/menu-level/menu-level-1b/menu-level-2b/menu-level-3b"));

export const dashboardRoutes: RouteObject[] = [
	{
		path: "/",
		element: (
			<AuthGuard>
				<Suspense fallback={<LineLoading />}>
					{/* outlet inside DashboardLayout */}
					<DashboardLayout />
				</Suspense>
			</AuthGuard>
		),
		children: [
			{ index: true, element: <PermittedHomepage /> },
			{
				path: "dashboard",
				children: [
					{ index: true, element: <WorkbenchPage /> },
					{ path: "workbench", element: <WorkbenchPage /> },
					{ path: "analysis", element: <AnalysisPage /> },
				],
			},
			{
				path: "components",
				children: [
					{ index: true, element: <AnimatePage /> },
					{ path: "animate", element: <AnimatePage /> },
					{ path: "scroll", element: <ScrollPage /> },
					{ path: "multi-language", element: <MultiLanguagePage /> },
					{ path: "icon", element: <IconPage /> },
					{ path: "upload", element: <UploadPage /> },
					{ path: "chart", element: <ChartPage /> },
					{ path: "toast", element: <ToastPage /> },
				],
			},
			{
				path: "functions",
				children: [
					{ index: true, element: <Navigate to="clipboard" replace /> },
					{ path: "clipboard", element: <ClipboardPage /> },
					{ path: "token-expired", element: <TokenExpiredPage /> },
				],
			},
			{
				path: "pw_management",
				children: [
					{ index: true, element: <Navigate to="merchant" replace /> },
					{ path: "merchant", element: <MerchantPage /> },
					{ path: "wallets", element: <WalletsPage /> },
					{ path: "rate", element: <RatePage /> },
					{ path: "pos_event", element: <PosEventPage /> },
					{ path: "fiat_distribution", element: <FiatDistributionPage /> },
					{ path: "payout_gateway_balances", element: <PayoutGatewayBalancesPage /> },
					{ path: "manual_distribution", element: <DistributionPage /> },
					{ path: "withdrawal_orders", element: <WithdrawalOrdersPage /> },
					{ path: "checkout_orders", element: <CheckoutOrdersPage /> },
				],
			},
			{
				path: "management",
				children: [
					{ index: true, element: <Navigate to="user" replace /> },
					{
						path: "user",
						children: [
							{ index: true, element: <Navigate to="profile" replace /> },
							{ path: "profile", element: <ProfilePage /> },
							{ path: "account", element: <AccountPage /> },
						],
					},
					{
						path: "system",
						children: [
							{ index: true, element: <Navigate to="organization" replace /> },
							{ path: "organization", element: <OrganizationPage /> },
							{ path: "permission", element: <PermissioPage /> },
							{ path: "role", element: <RolePage /> },
							{ path: "user", element: <UserPage /> },
						],
					},
				],
			},
			{
				path: "error",
				children: [
					{ index: true, element: <Navigate to="403" replace /> },
					{ path: "403", element: <Page403 /> },
					{ path: "404", element: <Page404 /> },
					{ path: "500", element: <Page500 /> },
				],
			},
			{
				path: "menu_level",
				children: [
					{ index: true, element: <Navigate to="menu_level_1a" replace /> },
					{ path: "menu_level_1a", element: <MenuLevel1a /> },
					{
						path: "menu_level_1b",
						children: [
							{ index: true, element: <Navigate to="menu_level_2a" replace /> },
							{ path: "menu_level_2a", element: <MenuLevel2a /> },
							{
								path: "menu_level_2b",
								children: [
									{ index: true, element: <Navigate to="menu_level_3a" replace /> },
									{ path: "menu_level_3a", element: <MenuLevel3a /> },
									{ path: "menu_level_3b", element: <MenuLevel3b /> },
								],
							},
						],
					},
				],
			},
		],
	},
];
